import { css } from '@emotion/react';
import { Box, Text, Title } from '@inflearn/ds-react-next';
import { Trans, useTranslation } from 'next-i18next';

import { LANDING_COLOR, LANDING_FONT } from './constants';

type TitleKey =
  | 'ai_interviews.landing.past.title'
  | 'ai_interviews.landing.quick.title'
  | 'ai_interviews.landing.chat.title'
  | 'ai_interviews.landing.report.title'
  | 'ai_interviews.landing.how.title'
  | 'ai_interviews.landing.review.title'
  | 'ai_interviews.landing.faq.title';

type DescriptionKey = 'ai_interviews.landing.chat.desc' | 'ai_interviews.landing.report.desc';

type SectionHeadProps = {
  id: string;
  /** `<ac>…</ac>` 구간을 강조색으로 표시한다 */
  titleKey: TitleKey;
  step?: number;
  descriptionKey?: DescriptionKey;
  tone?: 'light' | 'dark';
};

export const SectionHead = ({
  id,
  titleKey,
  step,
  descriptionKey,
  tone = 'light',
}: SectionHeadProps) => {
  const { t } = useTranslation();
  const dark = tone === 'dark';

  return (
    <Box css={styles.head}>
      {step ? (
        <Text component="span" css={styles.step}>
          {t('ai_interviews.landing.step.badge', { step })}
        </Text>
      ) : null}
      <Title order={2} id={id} css={[styles.title, dark ? styles.titleDark : null]}>
        <Trans
          i18nKey={titleKey}
          components={{
            ac: <em css={dark ? styles.accentDark : styles.accent} />,
            br: <br />,
          }}
        />
      </Title>
      {descriptionKey ? <Text css={styles.description}>{t(descriptionKey)}</Text> : null}
    </Box>
  );
};

const styles = {
  head: css({
    textAlign: 'center',
  }),
  step: css({
    display: 'inline-block',
    marginBottom: 18,
    padding: '7px 16px',
    borderRadius: 999,
    background: LANDING_COLOR.ink,
    color: '#fff',
    fontSize: LANDING_FONT.label,
    fontWeight: 800,
    letterSpacing: '0.06em',
  }),
  title: css({
    margin: 0,
    color: LANDING_COLOR.ink,
    fontSize: LANDING_FONT.sectionTitle,
    fontWeight: 800,
    lineHeight: 1.25,
    letterSpacing: '-0.04em',
    textWrap: 'balance',
    wordBreak: 'keep-all',
  }),
  titleDark: css({
    color: '#fff',
  }),
  accent: css({
    fontStyle: 'normal',
    color: LANDING_COLOR.mintDeep,
  }),
  accentDark: css({
    fontStyle: 'normal',
    color: LANDING_COLOR.mint,
  }),
  description: css({
    marginTop: 14,
    color: LANDING_COLOR.body,
    fontSize: LANDING_FONT.introBody,
    lineHeight: 1.7,
    wordBreak: 'keep-all',
  }),
};
